import { useState, useCallback, useEffect } from 'react';
import { getApiClient } from '../api/apiClient';
import { useNavigate } from 'react-router-dom';
import { takePostLoginRedirect } from '../utils/postLoginRedirect';

const API_KEY = 'bc836aa4-0419-491d-92f4-52e10e4edea5';
const apiClientIdentity = getApiClient('identity');

const useOtpAuth = () => {
  const navigate = useNavigate();
  const [mobileNumber, setMobileNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    const storedMobileNumber = localStorage.getItem('mobileNumber');
    if (storedMobileNumber) {
      setMobileNumber(storedMobileNumber);
    }
  }, []);

  const startCountdown = useCallback(() => {
    setResendDisabled(true);
    setCountdown(30);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setResendDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const validateMobileNumber = (number) => {
    return /^[0-9]{10}$/.test(number);
  };

  const validateOtp = (otp) => {
    return /^[0-9]{4}$/.test(otp);
  };

  const handleSendOtp = async () => {
    setError('');
    if (!validateMobileNumber(mobileNumber)) {
      setError('Please enter a valid 10-digit mobile number');
      return false;
    }
    setLoading(true);
    try {
      const response = await apiClientIdentity.post(
        '/otp/dashboardOtp',
        { mobileNumber },
        {
          headers: {
            'Content-Type': 'application/json',
            'accept': '*/*',
            'X-API-Key': API_KEY,
            'X-Client-Type': 'MobileApp',
          }
        }
      );
      if (response.data?.success) {
        localStorage.setItem('mobileNumber', mobileNumber);
        setOtp('');
        startCountdown();
        setTimeout(() => {
          navigate('/verify-otp', { replace: true });
        }, 100);
        return true;
      }
      return false;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to send OTP. Please try again.';
      setError(errorMessage);
      console.error('OTP Send Error:', err.response || err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError('');
    if (!validateOtp(otp)) {
      setError('Please enter a valid 4-digit OTP');
      return false;
    }
    const storedMobileNumber = localStorage.getItem('mobileNumber');
    if (!storedMobileNumber) {
      setError('Session expired. Please try again.');
      navigate('/login', { replace: true });
      return false;
    }
    setLoading(true);
    try {
      const response = await apiClientIdentity.post(
        '/otp/verify',
        {
          mobileNumber: storedMobileNumber,
          otp,
          referredBy: '',
          firstName: '',
          lastName: '',
          referralId: '3fa85f64-5717-4562-b3fc-2c963f66afa6'
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'accept': '*/*',
            'X-API-Key': API_KEY,
            'X-Client-Type': 'MobileApp'
          }
        }
      );
      if (response.data?.success) {
        const userData = response.data.data;
        if (userData) {
          const fullName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
          localStorage.setItem('userName', fullName || 'User');
          if (userData.userId) {
            localStorage.setItem('userId', userData.userId);
          }
          if (userData.token?.accessToken) {
            localStorage.setItem('accessToken', userData.token.accessToken);
          }
        } else {
          localStorage.setItem('userName', 'User');
        }
        localStorage.removeItem('mobileNumber');
        navigate(takePostLoginRedirect() || '/dashboard', { replace: true });
        return true;
      }
      return false;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Invalid OTP. Please try again.';
      setError(errorMessage);
      console.error('OTP Verification Error:', err.response || err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const resetState = () => {
    setMobileNumber('');
    setOtp('');
    setError('');
    setLoading(false);
    setResendDisabled(false);
    setCountdown(0);
  };

  return {
    mobileNumber,
    setMobileNumber,
    otp,
    setOtp,
    error,
    loading,
    resendDisabled,
    countdown,
    handleSendOtp,
    handleVerifyOtp,
    resetState
  };
};

export default useOtpAuth; 